// src/services/azureRelationsService.js
// Azure Table Storage Service für Project Relations/Network

const AZURE_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "projectRelations",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

// Relation Types
export const RELATION_TYPES = {
  // Project Relations
  PROJECT_TAG: "project_tag",
  PROJECT_PARTNER: "project_partner",
  PROJECT_PERSON: "project_person",
  PROJECT_PROJECT: "project_project",

  // Partner Relations
  PARTNER_PERSON: "partner_person", // Employment

  // Specific Roles
  AUFTRAGGEBERIN: "auftraggeberin", // Updated from AUFTRAGGEBER
  PARTNER: "partner",
  CONSULTANT: "consultant",
  CONTRACTOR: "contractor",

  // Internal Relations
  COLLABORATION: "collaboration",
  DEPENDENCY: "dependency",
  SEQUENCE: "sequence",
  SIMILAR: "similar",
};

// Helper functions for data cleaning
const cleanValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  return String(value);
};

const cleanNumber = (value) => {
  if (value === null || value === undefined || value === "" || isNaN(value)) {
    return 1.0;
  }
  return Number(value);
};

// Generate unique relation ID
const generateRelationId = (
  sourceType,
  sourceId,
  targetType,
  targetId,
  relationType
) => {
  return `${sourceType}_${sourceId}_${targetType}_${targetId}_${relationType}_${Date.now()}`;
};

// Relation erstellen
export const createRelation = async (relationData) => {
  try {
    const now = new Date().toISOString();

    // Validate required fields
    if (
      !relationData.sourceType ||
      !relationData.sourceId ||
      !relationData.targetType ||
      !relationData.targetId ||
      !relationData.relationType
    ) {
      throw new Error("Source, Target und RelationType sind erforderlich");
    }

    // EINFACH: Verwende neue generateRelationId mit Timestamp
    const relationId = generateRelationId(
      relationData.sourceType,
      relationData.sourceId,
      relationData.targetType,
      relationData.targetId,
      relationData.relationType
    );

    const entity = {
      PartitionKey: "relation",
      RowKey: relationId,
      SourceType: cleanValue(relationData.sourceType),
      SourceId: cleanValue(relationData.sourceId),
      TargetType: cleanValue(relationData.targetType),
      TargetId: cleanValue(relationData.targetId),
      RelationType: cleanValue(relationData.relationType),
      Function: cleanValue(relationData.function || ""),
      Description: cleanValue(relationData.description || ""),
      Weight: cleanNumber(relationData.weight || 1.0),
      Visibility: relationData.visibility !== false, // Default true
      CreatedAt: now,
      UpdatedAt: now,
    };

    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='relation',RowKey='${relationId}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Azure Error Details:", errorText);
      throw new Error(`Azure Save Error: ${response.status} - ${errorText}`);
    }

    return {
      id: relationId,
      ...relationData,
      createdAt: now,
      updatedAt: now,
    };
  } catch (error) {
    console.error("Create Relation Error:", error);
    throw error;
  }
};

// Relation löschen
export const deleteRelation = async (relationId) => {
  try {
    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='relation',RowKey='${relationId}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        "If-Match": "*",
      },
    });

    if (!response.ok && response.status !== 404) {
      const errorText = await response.text();
      throw new Error(`Azure Delete Error: ${response.status} - ${errorText}`);
    }

    return true;
  } catch (error) {
    console.error("Delete Relation Error:", error);
    throw error;
  }
};

// Alle Relations laden
export const getAllRelations = async () => {
  try {
    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      headers: { Accept: "application/json;odata=nometadata" },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Azure Error:", errorText);
      throw new Error(`Azure Table Error: ${response.status}`);
    }

    const data = await response.json();

    return data.value.map((item) => ({
      id: item.RowKey,
      sourceType: item.SourceType,
      sourceId: item.SourceId,
      targetType: item.TargetType,
      targetId: item.TargetId,
      relationType: item.RelationType,
      function: item.Function || "",
      description: item.Description || "",
      weight: item.Weight || 1.0,
      visibility: item.Visibility !== false,
      createdAt: item.CreatedAt,
      updatedAt: item.UpdatedAt,
    }));
  } catch (error) {
    console.error("Load Relations Error:", error);
    return [];
  }
};

// Relations für ein Projekt laden
export const getProjectRelations = async (projectId) => {
  try {
    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}?${AZURE_CONFIG.sasToken}&$filter=SourceId eq '${projectId}' or TargetId eq '${projectId}'`;

    const response = await fetch(url, {
      headers: { Accept: "application/json;odata=nometadata" },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Azure Table Error: ${response.status}`);
    }

    const data = await response.json();

    return data.value.map((item) => ({
      id: item.RowKey,
      sourceType: item.SourceType,
      sourceId: item.SourceId,
      targetType: item.TargetType,
      targetId: item.TargetId,
      relationType: item.RelationType,
      function: item.Function || "",
      description: item.Description || "",
      weight: item.Weight || 1.0,
      visibility: item.Visibility !== false,
      createdAt: item.CreatedAt,
      updatedAt: item.UpdatedAt,
    }));
  } catch (error) {
    console.error("Load Project Relations Error:", error);
    return [];
  }
};

// Relations nach Typ laden
export const getRelationsByType = async (relationType) => {
  try {
    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}?${AZURE_CONFIG.sasToken}&$filter=RelationType eq '${relationType}'`;

    const response = await fetch(url, {
      headers: { Accept: "application/json;odata=nometadata" },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Azure Table Error: ${response.status}`);
    }

    const data = await response.json();

    return data.value.map((item) => ({
      id: item.RowKey,
      sourceType: item.SourceType,
      sourceId: item.SourceId,
      targetType: item.TargetType,
      targetId: item.TargetId,
      relationType: item.RelationType,
      function: item.Function || "",
      description: item.Description || "",
      weight: item.Weight || 1.0,
      visibility: item.Visibility !== false,
      createdAt: item.CreatedAt,
      updatedAt: item.UpdatedAt,
    }));
  } catch (error) {
    console.error("Load Relations by Type Error:", error);
    return [];
  }
};

// Network Graph für bestimmte Projekte generieren
export const generateProjectNetwork = async (
  projectIds = [],
  relationTypes = []
) => {
  try {
    let relations = await getAllRelations();

    // Filter by project IDs if provided
    if (projectIds.length > 0) {
      relations = relations.filter(
        (rel) =>
          projectIds.includes(rel.sourceId) || projectIds.includes(rel.targetId)
      );
    }

    // Filter by relation types if provided
    if (relationTypes.length > 0) {
      relations = relations.filter((rel) =>
        relationTypes.includes(rel.relationType)
      );
    }

    // Only visible relations
    relations = relations.filter((rel) => rel.visibility);

    // Generate nodes from relations
    const nodeIds = new Set();
    relations.forEach((rel) => {
      nodeIds.add(`${rel.sourceType}:${rel.sourceId}`);
      nodeIds.add(`${rel.targetType}:${rel.targetId}`);
    });

    const nodes = Array.from(nodeIds).map((nodeId) => {
      const [type, id] = nodeId.split(":");
      return {
        id: nodeId,
        type: type,
        entityId: id,
        // Additional node properties can be loaded from respective services
      };
    });

    // Generate edges from relations
    const links = relations.map((rel) => ({
      source: `${rel.sourceType}:${rel.sourceId}`,
      target: `${rel.targetType}:${rel.targetId}`,
      type: rel.relationType,
      weight: rel.weight,
      function: rel.function,
      description: rel.description,
    }));

    return {
      nodes,
      links,
      metadata: {
        totalRelations: relations.length,
        relationTypes: [...new Set(relations.map((r) => r.relationType))],
        generatedAt: new Date().toISOString(),
      },
    };
  } catch (error) {
    console.error("Generate Project Network Error:", error);
    return { nodes: [], links: [], metadata: {} };
  }
};

// Bulk Relations erstellen (für Import/Migration)
export const createBulkRelations = async (relationsArray) => {
  try {
    const results = [];

    for (const relationData of relationsArray) {
      try {
        const result = await createRelation(relationData);
        results.push({ success: true, relation: result });
      } catch (error) {
        results.push({
          success: false,
          error: error.message,
          relationData,
        });
      }
    }

    return {
      total: relationsArray.length,
      successful: results.filter((r) => r.success).length,
      failed: results.filter((r) => !r.success).length,
      results,
    };
  } catch (error) {
    console.error("Create Bulk Relations Error:", error);
    throw error;
  }
};

// Relations für ein Entity löschen (beim Löschen von Projekten/Partnern/Personen)
export const deleteEntityRelations = async (entityType, entityId) => {
  try {
    const relations = await getAllRelations();
    const entityRelations = relations.filter(
      (rel) =>
        (rel.sourceType === entityType && rel.sourceId === entityId) ||
        (rel.targetType === entityType && rel.targetId === entityId)
    );

    const deletePromises = entityRelations.map((rel) => deleteRelation(rel.id));
    await Promise.all(deletePromises);

    return {
      deletedCount: entityRelations.length,
      deletedRelations: entityRelations.map((r) => r.id),
    };
  } catch (error) {
    console.error("Delete Entity Relations Error:", error);
    throw error;
  }
};

// Helper: Project-Tag Relation erstellen
export const addProjectTag = async (projectId, tagName) => {
  return await createRelation({
    sourceType: "project",
    sourceId: projectId,
    targetType: "tag",
    targetId: tagName.toLowerCase().replace(/\s+/g, "_"),
    relationType: RELATION_TYPES.PROJECT_TAG,
    description: `Tag: ${tagName}`,
  });
};

// Helper: Project-Partner Relation erstellen
export const addProjectPartner = async (
  projectId,
  partnerId,
  role,
  functionDescription = ""
) => {
  return await createRelation({
    sourceType: "project",
    sourceId: projectId,
    targetType: "partner",
    targetId: partnerId,
    relationType: role, // auftraggeberin, partner, consultant, etc.
    function: functionDescription,
    description: `${role}: ${functionDescription}`,
  });
};

// Helper: Project-Person Relation erstellen
export const addProjectPerson = async (
  projectId,
  personId,
  role,
  functionDescription = ""
) => {
  return await createRelation({
    sourceType: "project",
    sourceId: projectId,
    targetType: "person",
    targetId: personId,
    relationType: RELATION_TYPES.PROJECT_PERSON,
    function: `${role}: ${functionDescription}`,
    description: `Person im Projekt als ${role}`,
  });
};

// Helper: Partner-Person Relation erstellen (Employment)
export const addPartnerPerson = async (partnerId, personId, position = "") => {
  return await createRelation({
    sourceType: "partner",
    sourceId: partnerId,
    targetType: "person",
    targetId: personId,
    relationType: RELATION_TYPES.PARTNER_PERSON,
    function: position,
    description: `Anstellung: ${position}`,
  });
};

// Helper: Project-Project Relation erstellen
export const addProjectRelation = async (
  sourceProjectId,
  targetProjectId,
  relationType,
  description = ""
) => {
  return await createRelation({
    sourceType: "project",
    sourceId: sourceProjectId,
    targetType: "project",
    targetId: targetProjectId,
    relationType: relationType, // collaboration, dependency, sequence, similar
    description: description,
  });
};

// Get Project Tags
export const getProjectTags = async (projectId) => {
  const relations = await getProjectRelations(projectId);
  return relations.filter(
    (rel) =>
      rel.relationType === RELATION_TYPES.PROJECT_TAG &&
      rel.sourceType === "project" &&
      rel.sourceId === projectId
  );
};

// Get Project Partners
export const getProjectPartners = async (projectId) => {
  const relations = await getProjectRelations(projectId);
  return relations.filter(
    (rel) =>
      rel.targetType === "partner" &&
      rel.sourceType === "project" &&
      rel.sourceId === projectId
  );
};

// Get Project Persons
export const getProjectPersons = async (projectId) => {
  const relations = await getProjectRelations(projectId);
  return relations.filter(
    (rel) =>
      rel.targetType === "person" &&
      rel.sourceType === "project" &&
      rel.sourceId === projectId
  );
};

// Get Related Projects
export const getRelatedProjects = async (projectId) => {
  const relations = await getProjectRelations(projectId);
  return relations.filter(
    (rel) =>
      rel.relationType === RELATION_TYPES.PROJECT_PROJECT &&
      (rel.sourceType === "project" || rel.targetType === "project")
  );
};

// Statistics für Dashboard
export const getRelationStatistics = async () => {
  try {
    const relations = await getAllRelations();

    const stats = {
      total: relations.length,
      byType: {},
      bySourceType: {},
      byTargetType: {},
      visible: relations.filter((r) => r.visibility).length,
    };

    relations.forEach((rel) => {
      // Count by relation type
      stats.byType[rel.relationType] =
        (stats.byType[rel.relationType] || 0) + 1;

      // Count by source type
      stats.bySourceType[rel.sourceType] =
        (stats.bySourceType[rel.sourceType] || 0) + 1;

      // Count by target type
      stats.byTargetType[rel.targetType] =
        (stats.byTargetType[rel.targetType] || 0) + 1;
    });

    return stats;
  } catch (error) {
    console.error("Get Relation Statistics Error:", error);
    return {
      total: 0,
      byType: {},
      bySourceType: {},
      byTargetType: {},
      visible: 0,
    };
  }
};

// Konfiguration aktualisieren
export const updateConfig = (newConfig) => {
  Object.assign(AZURE_CONFIG, newConfig);
};
